<script setup>
import { computed } from 'vue';
import { useModal } from 'vue-final-modal';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import FormFilter from '~/forms/components/form-filter.vue';
import { useFormsStore } from '~/forms/store/forms.store';
import TaskFilters from '~/tasks/components/organisms/tasks-filters.vue';
import { useTasksStore } from '~/tasks/store/tasks.store';
import LabelSelectorPopup from '~/terra/components/settings/label-selector-popup.vue';
import ArcGISLayersPopup from '~/terra/components/settings/arcgis-layers-popup.vue';
import { useTerraStore } from '../store/terra.store';
import { useCommonStore } from '~/common/stores/common.store.js';

const emit = defineEmits(['saveView', 'setSymbols', 'toggleLabels']);

const { $services, $t, $toast, auth_store, route } = useCommonImports();

const terra_store = useTerraStore();
const common_store = useCommonStore();
const task_store = useTasksStore('terra_task_store');
const forms_store = useFormsStore('terra_form_store');
const task_filter_popup = useModal({
  component: TaskFilters,
  attrs: {
    filter_type: 'terra',
    onClose() {
      task_filter_popup.close();
    },
  },
});
const form_filter_popup = useModal({
  component: FormFilter,
  attrs: {
    filter_type: 'terra',
    onClose() {
      form_filter_popup.close();
    },
  },
});
const label_selector_popup = useModal({
  component: LabelSelectorPopup,
  attrs: {
    onClose() {
      label_selector_popup.close();
    },
    onUpdateLabels() {
      emit('toggleLabels');
      label_selector_popup.close();
    },
  },
});

const arcgis_layers_popup = useModal({
  component: ArcGISLayersPopup,
  attrs: {
    onClose() {
      arcgis_layers_popup.close();
    },
    onSaved() {
      arcgis_layers_popup.close();
    },
  },
});

const form = ref({
  enable_naavix_ai: terra_store.container?.properties?.enable_naavix_ai || false,
});

const arcgis_enable = ref(terra_store.container?.properties?.arcgis?.enable || false);

const settings = computed(() => {
  return ({
    ...terra_store.settings,
    tasks_cluster: terra_store.tasks_cluster,
    forms_cluster: terra_store.forms_cluster,
  });
});

function openTasksFiltersPopup() {
  task_filter_popup.patchOptions({
    attrs: {
      fields: ['Group by', 'Status', 'Priority', 'Tags', 'Category', 'Assignees', 'Created By', 'Due Date', 'Starred', 'Archived'],
      filters: terra_store.tasks_cluster.filters,
      task_store,
      onFilter(val) {
        logger.log(val);
        terra_store.tasks_cluster.filters = val;
        emit('setSymbols');
        emit('saveView');
        task_filter_popup.close();
      },
    },
  });
  task_filter_popup.open();
}

function openFormFiltersPopup() {
  form_filter_popup.patchOptions({
    attrs: {
      fields: ['Template', 'Tags', 'Status', 'Category', 'Assignees', 'Created By', 'Due Date', 'Rolled Back'],
      filters: terra_store.forms_cluster.filters,
      forms_store,
      onFilter(val) {
        terra_store.forms_cluster.filters = { ...val, status: val?.submission_status || [] };
        emit('setSymbols');
        emit('saveView');
        form_filter_popup.close();
      },
    },
  });
  form_filter_popup.open();
}

function updateSettings(key, value) {
  const new_settings = settings.value;
  new_settings[key] = value;
  terra_store.settings = new_settings;
  if (key === 'filter_task_form_with_target_element')
    terra_store.terra_track_events('Settings - Filter for components');
  else if (key === 'display_labels')
    terra_store.terra_track_events('Settings - Show label');

  emit('saveView');
}
function toggleActive(key) {
  terra_store[key].active = !terra_store[key].active;
  const status = terra_store[key].active ? 'enabled' : 'hidden';
  if (key === 'tasks_cluster')
    terra_store.terra_track_events(`Tasks ${status}`);
  else if (key === 'forms_cluster')
    terra_store.terra_track_events(`Forms ${status}`);
  emit('setSymbols');
  emit('saveView');
}

function toggleCluster(key) {
  terra_store[key].cluster = !terra_store[key].cluster;
  const status = terra_store[key].cluster ? 'enabled' : 'disabled';
  if (key === 'tasks_cluster')
    terra_store.terra_track_events(`Tasks cluster ${status}`);
  else if (key === 'forms_cluster')
    terra_store.terra_track_events(`Forms cluster ${status}`);
  emit('setSymbols');
  emit('saveView');
}

async function toggleNaavixAI() {
  if (form.value.enable_naavix_ai === terra_store.container?.properties?.enable_naavix_ai) {
    return; // No change needed
  }
  try {
    const container = terra_store.container;
    if (!container.properties) {
      container.properties = {
        enable_naavix_ai: true,
      };
    }
    container.properties.enable_naavix_ai = !container.properties.enable_naavix_ai;
    const { data } = await $services.containers.patch({
      asset_id: route.params.asset_id,
      container_id: route.params.id,
      body: {
        container,
      },
    });
    terra_store.container.properties = data.container.properties;
  }
  catch (error) {
    logger.error('Error toggling NaaviX AI:', error);
    $toast({
      type: 'error',
      title: $t('Something went wrong'),
      message: $t('Please try again later.'),
    });
  }
  finally {
    form.value.enable_naavix_ai = terra_store.container?.properties?.enable_naavix_ai || false;
  }
}

async function toggleArcgis() {
  try {
    const container = terra_store.container;
    if (!container.properties) container.properties = {};
    if (!container.properties.arcgis) container.properties.arcgis = {};
    container.properties.arcgis.enable = !container.properties.arcgis.enable;
    const { data } = await $services.containers.patch({
      asset_id: route.params.asset_id,
      container_id: route.params.id,
      body: { container },
    });
    terra_store.container.properties = data.container.properties;
    arcgis_enable.value = terra_store.container?.properties?.arcgis?.enable || false;
  }
  catch (error) {
    logger.error('Error toggling ArcGIS:', error);
    arcgis_enable.value = terra_store.container?.properties?.arcgis?.enable || false;
    $toast({
      type: 'error',
      title: $t('Something went wrong'),
      message: $t('Please try again later.'),
    });
  }
}
</script>

<template>
  <div class="p-4">
    <div class="mb-6">
      <p class="font-semibold">
        {{ $t('Settings') }}
      </p>
    </div>
    <div class="flex cursor-pointer items-center py-1.5 rounded-lg justify-between hover:bg-gray-50">
      <div class="flex items-center">
        <HawkCheckbox id="labels" :model-value="settings.display_labels" @input="updateSettings('display_labels', !settings.display_labels);emit('toggleLabels')">
          {{ $t('Show labels') }}
        </HawkCheckbox>
      </div>
      <div class="cursor-pointer" @click="label_selector_popup.open()">
        <IconHawkSettingsOne class="w-[16px] h-[16px] text-gray-500" />
      </div>
    </div>
    <div class="flex cursor-pointer items-center py-1.5 rounded-lg justify-between hover:bg-gray-50">
      <HawkCheckbox id="fly" :model-value="settings.fly" @input="updateSettings('fly', !settings.fly)">
        {{ $t('Fly to bounds') }}
      </HawkCheckbox>
    </div>

    <div class="flex cursor-pointer items-center py-1.5 rounded-lg justify-between hover:bg-gray-50">
      <HawkCheckbox id="fly" :model-value="settings.show_counts" @input="updateSettings('show_counts', !settings.show_counts)">
        {{ $t('Show counts') }}
      </HawkCheckbox>
    </div>

    <!-- ArcGIS overlay (RIL only) -->
    <div v-if="!common_store.is_production" class="flex cursor-pointer items-center py-1.5 rounded-lg justify-between hover:bg-gray-50">
      <div class="flex items-center">
        <HawkCheckbox id="arcgis_enable" :model-value="arcgis_enable" @input="toggleArcgis">
          {{ $t('Show ArcGIS map') }}
        </HawkCheckbox>
      </div>
      <div class="cursor-pointer" @click="arcgis_layers_popup.open()">
        <IconHawkSettingsOne class="w-[16px] h-[16px] text-gray-500" />
      </div>
    </div>

    <template v-if="auth_store.has_tasks_access || auth_store.has_forms_access">
      <hr class="border-b border-gray-200 my-2.5">
      <div v-if="auth_store.has_tasks_access" class="flex cursor-pointer items-center py-1.5 rounded-lg justify-between hover:bg-gray-50">
        <div class="flex items-center ">
          <HawkCheckbox id="tasks" :model-value="settings.tasks_cluster.active" @input="toggleActive('tasks_cluster')">
            {{ $t('Tasks') }}
          </HawkCheckbox>
        </div>
        <div class="cursor-pointer" @click="openTasksFiltersPopup">
          <IconHawkSettingsOne class="w-[16px] h-[16px] text-gray-500" />
        </div>
      </div>
      <div v-if="auth_store.has_tasks_access && settings.tasks_cluster.active" class="flex cursor-pointer items-center py-1.5 rounded-lg justify-between hover:bg-gray-50 ml-6">
        <HawkCheckbox id="tasks_cluster" :model-value="settings.tasks_cluster.cluster" @input="toggleCluster('tasks_cluster')">
          {{ $t('Show task counts') }}
        </HawkCheckbox>
      </div>
      <div v-if="auth_store.has_forms_access" class="flex cursor-pointer items-center py-1.5 rounded-lg justify-between hover:bg-gray-50">
        <div class="flex items-center">
          <HawkCheckbox id="forms" :model-value="settings.forms_cluster.active" @input="toggleActive('forms_cluster')">
            {{ $t('Forms') }}
          </HawkCheckbox>
        </div>
        <div class="cursor-pointer" @click="openFormFiltersPopup">
          <IconHawkSettingsOne class="w-[16px] h-[16px] text-gray-500" />
        </div>
      </div>
      <div v-if="auth_store.has_forms_access && settings.forms_cluster.active" class="flex cursor-pointer items-center py-1.5 rounded-lg justify-between hover:bg-gray-50 ml-6">
        <HawkCheckbox id="forms_cluster" :model-value="settings.forms_cluster.cluster" @input="toggleCluster('forms_cluster')">
          {{ $t('Show form counts') }}
        </HawkCheckbox>
      </div>
      <div class="flex cursor-pointer items-center py-1.5 rounded-lg justify-between hover:bg-gray-50">
        <HawkCheckbox id="filter_task_form_with_target_element" :disabled="!terra_store.should_use_boundary" :model-value="settings.filter_task_form_with_target_element" @input="updateSettings('filter_task_form_with_target_element', !settings.filter_task_form_with_target_element); emit('setSymbols')">
          {{ $t('Filter for components') }}
        </HawkCheckbox>
      </div>
    </template>

    <template v-if="auth_store.is_hacker_user || auth_store.is_manager_team_user">
      <Vueform v-model="form" size="sm" sync>
        <div class="col-span-12">
          <div>
            <hr class="border-b border-gray-200 my-2.5">
            <div class="flex items-start justify-between mb-4">
              <div class="w-[250px]">
                <div class="text-sm font-semibold text-gray-700 mb-1">
                  NaaviX AI
                </div>
                <div class="text-xs text-gray-700">
                  {{ $t('Enable agent to') }}
                  <span class="lowercase">
                    {{ $t('Monitor solar construction progress, analyze performance metrics, and stay ahead of project timelines.') }}
                  </span>
                </div>
              </div>
              <ToggleElement
                name="enable_naavix_ai"
                @change="toggleNaavixAI"
              />
            </div>
          </div>
        </div>
      </Vueform>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.appearance-auto {
  appearance: auto !important;
}
</style>
